// W40.1 (2026-10-21, M1): the approved file-shortcuts row that sits
// between the identity row and the eight customer tabs on the customer
// shell screens.
//
// Markup READ from 01-Current-Action-Consistency-Review-v7.html:
//   <div class="cx-file-shortcuts"><span>Sarah’s customer file</span>
//     <button type="button" data-cx-view="customer">Customer &amp; Addresses</button>
//     <button type="button" data-cx-view="preferences">Edit Preferences</button></div>
// The approved files carry the row on 01, 03, 04, 06, 07, 08, 09 and 10
// (MEASURED: `class="cx-file-shortcuts"` present once in each of those
// files and in none of 05, 11-22 or the companions), so only those
// screens mount it.
//
// Navigation only, no data: the two buttons open the existing
// /customer-addresses/:id and /edit-preferences/:id routes with the same
// review-return state the customer tabs carry, so "Finish & Next" /
// "My work" on the opened screen come back to the screen the rep left.
// The label uses the customer's real first name; while the profile is
// still loading (no first name yet) it reads "Customer file" rather than
// inventing one.
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getReviewReturnTarget } from './ReviewState.js';

export interface FileShortcutsProps {
  customerId: string;
  /** Real first name; empty while the profile is loading or unknown. */
  firstName: string;
}

/** "Jodi’s customer file", or "Customer file" when no first name is known. */
export function fileShortcutsLabel(firstName: string): string {
  const name = firstName.trim();
  return name.length > 0 ? `${name}’s customer file` : 'Customer file';
}

export function FileShortcuts({ customerId, firstName }: FileShortcutsProps): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const open = (path: 'customer-addresses' | 'edit-preferences'): void => {
    const target = getReviewReturnTarget(location.state, customerId, location.pathname + location.search);
    navigate(`/${path}/${encodeURIComponent(customerId)}`, { state: target.state });
  };
  return (
    <div className="cx-file-shortcuts" data-component="file-shortcuts">
      <span>{fileShortcutsLabel(firstName)}</span>
      <button type="button" data-cx-view="customer" onClick={() => open('customer-addresses')}>
        Customer &amp; Addresses
      </button>
      <button type="button" data-cx-view="preferences" onClick={() => open('edit-preferences')}>
        Edit Preferences
      </button>
    </div>
  );
}
